import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // The refresh token now travels as an httpOnly cookie the API sets on /auth/login|register|
  // refresh (see AuthController's SetRefreshTokenCookie) - the browser only attaches it if the
  // request is made with credentials.
  withCredentials: true,
});

// Token refresh queue to prevent race conditions
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedQueue = [];
};

// Request interceptor - add auth token and tenant header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;
    const tenantId = useTenantStore.getState().tenantId;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors with proper token refresh queue
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - token expired
    // Skip refresh logic for auth endpoints (login/refresh failures should propagate directly)
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Another refresh is in progress - queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject: (err) => {
              reject(err);
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // No body - the real refresh token lives in the httpOnly cookie the API set on login,
        // attached automatically because the instance is created with withCredentials: true. It's
        // never read from (or stored in) frontend JS state, so there's no in-memory copy that can go
        // stale between tabs the way there used to be - see authStore.ts's history comment on the
        // now-removed refreshToken field.
        const response = await api.post('/auth/refresh');

        // The endpoint returns "accessToken" (TokenResponse), not "token". Reading the wrong key
        // yielded undefined, which was then stored as the token and sent as "Bearer undefined" -
        // so every retry 401'd, triggered another refresh, and the app sat in a refresh loop where
        // no data ever loaded until the user signed in again.
        const { accessToken } = response.data.data;

        // If the shape ever changes again, fail loudly into the logout path below instead of
        // silently looping.
        if (!accessToken) {
          throw new Error('Refresh response did not include an access token');
        }

        useAuthStore.getState().setTokens(accessToken);

        // Process queued requests with new token
        processQueue(null, accessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - reject all queued requests and logout
        processQueue(refreshError, null);
        useAuthStore.getState().logout();
        useTenantStore.getState().clearTenant();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    return Promise.reject(error);
  }
);

export default api;

// Named export for backward compatibility (some hooks use this name)
export const axiosInstance = api;

// Re-export types from common.types for backward compatibility
export type { ApiResponse, PaginatedResponse } from './types/common.types';
